import { persist } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';
import { useStore } from 'zustand';

export type SortKey =
    | 'patient'
    | 'ssn'
    | 'onboarding'
    | 'nextAppointment'
    | 'status'
    | 'trainingPlan'
    | 'teleRehaProgram'
    | 'documents'
    | 'therapyBilling'
    | 'billed'
    | 'therapist';

export type SortDir = 'asc' | 'desc';
export type SortState = { key: SortKey; dir: SortDir } | null;

export type FilterState = {
  patientQuery: string;
  onboarding: '' | 'onboarded' | 'notOnboarded';
  status: '' | string;
  trainingPlan: '' | 'hasPlan' | 'noPlan';
  teleRehaProgramId: '' | string;
}

export const DEFAULT_FILTERS: FilterState = {
  patientQuery: '',
  onboarding: '',
  status: '',
  trainingPlan: '',
  teleRehaProgramId: ''
};

type PatientTableState = {
  filters: FilterState;
  sort: SortState;

  setPatientQuery: (value: string) => void;
  setFilter: <K extends keyof FilterState>(key: K, value: FilterState[K]) => void;

  toggleSort: (key: SortKey) => void;
  
  resetFilters: () => void;
  resetAll: () => void;
}

export const createPatientTableStore = (userId: string, page: string) => {
  const storageKey: string = `patientsTable:v2:${page}:${userId}`;
  
  return createStore<PatientTableState>()(
    persist((set, get) => ({
      filters: DEFAULT_FILTERS,
      sort: { key: 'patient', dir: 'asc' },
      
      setPatientQuery: (value: string) =>
        set((state: PatientTableState) =>
          state.filters.patientQuery === value
            ? state
            : { filters: { ...state.filters, patientQuery: value } }
        ),
      
      setFilter: (key, value) =>
        set((state: PatientTableState) => {
          if (state.filters[key] === value) {
            return state;
          }
          return { filters: { ...state.filters, [key]: value } };
        }),
      
      toggleSort: (key: SortKey) =>
        set((state: PatientTableState) => {
          const prev: SortState = state.sort;
          if (!prev || prev.key !== key) {
            return { sort: { key, dir: 'asc' } };
          }
          return { sort: { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } };
        }),
      
      resetFilters: () =>
        set((state: PatientTableState) => ({
          filters: {
            ...state.filters,
            onboarding: '',
            status: '',
            trainingPlan: '',
            teleRehaProgramId: '',
          },
        })),
      
      resetAll: () => set({ filters: DEFAULT_FILTERS, sort: { key: 'patient', dir: 'asc' } }),
    }),
    {
      name: storageKey,
      partialize: (state: PatientTableState) => ({ filters: state.filters, sort: state.sort }),
    },
    ),
  )
};

export type PatientsTableStore = ReturnType<typeof createPatientTableStore>;
export const usePatientTableSelector = <T>(
  store: PatientsTableStore,
  selector: (s: PatientTableState) => T
): T => useStore(store, selector);